import React from "react";
import { HealthIcon } from "./icons";
import { healthSnapshot } from "./snapshot";
import "./health-board.css";

/**
 * Application health and release board.
 * The snapshot may be the design demo or a live health reading.
 * @param {{
 *   snapshot?: typeof healthSnapshot,
 *   onEnvironmentChange?: (environmentId: string) => void
 * }} props
 * @returns {JSX.Element}
 */
export function HealthBoard({ snapshot = healthSnapshot, onEnvironmentChange }) {
  return (
    <article className="health-board w-full overflow-hidden rounded-2xl border border-[#e4e7ec] bg-white text-[#202632] leading-[normal]">
      <BoardHeader
        snapshot={snapshot}
        onEnvironmentChange={onEnvironmentChange}
      />
      <ServiceHealth snapshot={snapshot} />
      <ReleaseProgression release={snapshot.release} />
      <IncidentAndSafeguards
        incident={snapshot.incident}
        safeguards={snapshot.safeguards}
      />
      <BoardFooter footer={snapshot.footer} />
    </article>
  );
}

/**
 * @param {{
 *   snapshot: typeof healthSnapshot,
 *   onEnvironmentChange?: (environmentId: string) => void
 * }} props
 */
function BoardHeader({ snapshot, onEnvironmentChange }) {
  const environments = Array.isArray(snapshot.environments)
    ? snapshot.environments
    : [];

  return (
    <header className="flex items-center justify-between gap-6 border-b border-[#e4e7ec] px-7 py-6">
      <div className="flex flex-col gap-[7px]">
        <div className="flex items-center gap-3">
          <h1 className="text-[22px] font-semibold leading-none">
            {snapshot.title}
          </h1>
          <span className="rounded-md bg-[#f8f9fb] px-2 py-1 text-[11px] font-semibold text-[#687180]">
            {snapshot.dataBadge}
          </span>
        </div>
        <p className="text-[13px] text-[#687180]">{snapshot.subtitle}</p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="flex items-center gap-1.5 text-[12px]">
          <HealthIcon name="layers" />
          {environments.length > 0 ? (
            <div className="flex items-center gap-1">
              {environments.map((environment, index) => (
                <span key={environment.id} className="flex items-center gap-1">
                  {index > 0 ? <span className="text-[#687180]">/</span> : null}
                  <button
                    type="button"
                    onClick={() => onEnvironmentChange?.(environment.id)}
                    className={
                      environment.id === snapshot.environmentId
                        ? "font-medium text-[#202632]"
                        : "text-[#687180]"
                    }
                  >
                    {environment.label}
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <span className="font-medium">{snapshot.environment}</span>
          )}
          <span className="text-[#687180]">/ {snapshot.region}</span>
        </div>
        <p className="text-[12px] text-[#687180]">{snapshot.capturedAt}</p>
      </div>
    </header>
  );
}

/**
 * @param {{ snapshot: typeof healthSnapshot }} props
 */
function ServiceHealth({ snapshot }) {
  return (
    <section className="flex flex-col gap-3.5 px-7 pb-6 pt-[22px]">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <h2 className="text-[14px] font-semibold">Service health</h2>
          <p className="text-[12px] text-[#687180]">{snapshot.serviceSummary}</p>
        </div>
        <p className="text-[11px] text-[#687180]">{snapshot.freshness}</p>
      </div>
      <div className="flex items-stretch gap-3.5">
        {snapshot.services.map((service) => (
          <ServiceCard key={service.id} service={service} />
        ))}
      </div>
    </section>
  );
}

/**
 * @param {{ service: (typeof healthSnapshot.services)[number] }} props
 */
function ServiceCard({ service }) {
  const attention = service.status === "degraded" || service.status === "down";
  const unknown = service.status === "unknown";

  return (
    <div
      className={`flex min-w-0 flex-1 flex-col gap-4 rounded-[10px] border bg-white p-[18px] ${
        attention ? "border-[#f0dcb4]" : "border-[#e4e7ec]"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex size-8 items-center justify-center rounded-lg bg-[#f8f9fb]">
            <HealthIcon name={service.icon} />
          </div>
          <div className="flex flex-col gap-[3px]">
            <p className="text-[14px] font-semibold leading-none">{service.name}</p>
            <p className="text-[11px] text-[#687180]">{service.platform}</p>
          </div>
        </div>
        <StatusBadge
          tone={attention ? "warning" : unknown ? "neutral" : "healthy"}
          icon={attention ? "triangleAlert" : unknown ? "info" : "checkBadge"}
          label={service.statusLabel}
        />
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <p className="text-[28px] font-semibold leading-none">{service.metric}</p>
          <p className="text-[12px] text-[#687180]">{service.metricLabel}</p>
        </div>
        <HealthIntervals intervals={service.intervals} />
      </div>

      <div className="flex flex-col gap-[9px]">
        <p
          className={`whitespace-pre-wrap text-[12px] ${
            service.detailTone === "warning" ? "text-[#9b650e]" : "text-[#687180]"
          }`}
        >
          {service.detail}
        </p>
        <div className="flex items-start justify-between gap-3 text-[11px]">
          <p className="text-[#687180]">{service.sloLabel}</p>
          <p className="font-medium">{service.sloValue}</p>
        </div>
      </div>

      <div className="flex items-start justify-between gap-3 border-t border-[#e4e7ec] pt-[11px] text-[10px] text-[#687180]">
        <p className="health-mono">{service.version}</p>
        <p>{service.updated}</p>
      </div>
    </div>
  );
}

/**
 * @param {{ intervals: { height: number, color: string }[] }} props
 */
function HealthIntervals({ intervals }) {
  if (!intervals?.length) {
    return null;
  }

  return (
    <div className="flex h-7 items-end gap-[3px]" aria-hidden="true">
      {intervals.map((interval, index) => (
        <span
          key={`${interval.height}-${index}`}
          className="w-1 rounded-[2px]"
          style={{ height: interval.height, backgroundColor: interval.color }}
        />
      ))}
    </div>
  );
}

/**
 * @param {{ release: typeof healthSnapshot.release }} props
 */
function ReleaseProgression({ release }) {
  return (
    <section className="flex flex-col gap-[18px] border-y border-[#e4e7ec] px-7 pb-6 pt-[23px]">
      <div className="flex items-start justify-between gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-[9px]">
            <HealthIcon name="github" />
            <h2 className="text-[14px] font-semibold">Release progression</h2>
            <StatusBadge
              tone={release.statusTone || "warning"}
              icon={release.statusIcon || "pauseBadge"}
              label={release.statusLabel}
            />
          </div>
          <div className="health-mono flex items-center gap-[9px] text-[12px]">
            <span>{release.repository}</span>
            <span className="text-[#4d5bd1]">{release.version}</span>
            <span className="text-[11px] text-[#687180]">{release.ref}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-[9px]">
          <TextLink label={release.linkLabel} href={release.linkHref} />
          <p className="text-[11px] text-[#687180]">{release.timing}</p>
        </div>
      </div>

      <div className="flex items-stretch gap-2">
        {release.stages.map((stage, index) => (
          <React.Fragment key={stage.id}>
            {index > 0 ? (
              <div className="flex shrink-0 items-center">
                <HealthIcon name="chevronRight" />
              </div>
            ) : null}
            <StageCard stage={stage} />
          </React.Fragment>
        ))}
      </div>

      <div className="flex flex-col gap-4 rounded-lg bg-[#f8f9fb] p-3.5 lg:flex-row lg:items-center lg:gap-6">
        <div className="flex w-full shrink-0 flex-col gap-[9px] lg:w-[280px]">
          <div className="flex items-start justify-between text-[11px]">
            <p className="font-medium">{release.rolloutLabel}</p>
            <p
              className={`font-semibold ${
                release.attention === false ? "text-[#237553]" : "text-[#9b650e]"
              }`}
            >
              {release.rolloutValue}
            </p>
          </div>
          <div className="h-[5px] w-full overflow-hidden rounded-full bg-[#e8ebf0]">
            <div
              className="h-[5px] rounded-full"
              style={{
                width: `${release.rolloutPercent}%`,
                backgroundColor:
                  release.attention === false ? "#237553" : "#9b650e",
              }}
            />
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
          <p className="text-[12px] font-medium">{release.guardrailTitle}</p>
          <p className="text-[11px] text-[#687180]">{release.guardrailDetail}</p>
        </div>
        <div className="flex flex-col items-start gap-[7px] lg:items-end">
          <StatusBadge
            tone={release.rollbackTone || "healthy"}
            icon={release.rollbackIcon || "rotateCcw"}
            label={release.rollbackLabel}
          />
          <p className="health-mono text-[10px] text-[#687180]">
            {release.previousStable}
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * @param {{ stage: (typeof healthSnapshot.release.stages)[number] }} props
 */
function StageCard({ stage }) {
  const paused = stage.status === "paused";
  const unknown = stage.status === "unknown";

  return (
    <div
      className={`flex min-w-0 flex-1 gap-2.5 rounded-lg p-3.5 ${
        paused ? "bg-[#fff6e5]" : "bg-[#f8f9fb]"
      }`}
    >
      <div
        className={`flex size-[22px] shrink-0 items-center justify-center rounded-full ${
          paused ? "bg-[#f8e5be]" : unknown ? "bg-[#e8ebf0]" : "bg-[#ddefe4]"
        }`}
      >
        <HealthIcon
          name={paused ? "pauseStage" : unknown ? "info" : "checkStage"}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="text-[12px] font-semibold">{stage.title}</p>
        <p className={`text-[11px] ${paused ? "text-[#9b650e]" : "text-[#687180]"}`}>
          {stage.detail}
        </p>
        <p className="text-[10px] text-[#687180]">{stage.timing}</p>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   incident: typeof healthSnapshot.incident,
 *   safeguards: typeof healthSnapshot.safeguards
 * }} props
 */
function IncidentAndSafeguards({ incident, safeguards }) {
  const open = incident.open !== false;

  return (
    <section className="flex flex-col gap-7 px-7 py-6 xl:flex-row">
      <div className="flex min-w-0 flex-1 flex-col gap-3.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-[14px] font-semibold">Active incident</h2>
            <span
              className={`rounded-md px-2 py-1 text-[11px] font-semibold ${
                open ? "bg-[#fff6e5] text-[#9b650e]" : "bg-[#f8f9fb] text-[#687180]"
              }`}
            >
              {incident.countLabel}
            </span>
          </div>
          {incident.linkLabel ? (
            <TextLink label={incident.linkLabel} href={incident.linkHref} />
          ) : null}
        </div>
        <div
          className={`flex flex-col gap-2.5 rounded-lg p-3.5 ${
            open ? "bg-[#fff6e5]" : "bg-[#f8f9fb]"
          }`}
        >
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-bold text-[#9b650e]">{incident.severity}</p>
            <p className="text-[11px] text-[#687180]">{incident.id}</p>
            <p className="text-[11px] font-medium text-[#9b650e]">{incident.state}</p>
          </div>
          <p className="text-[13px] font-semibold">{incident.title}</p>
          <p className="text-[12px] leading-normal text-[#687180]">{incident.summary}</p>
          <div className="flex items-center justify-between gap-3">
            {incident.owner ? (
              <div className="flex items-center gap-1.5">
                {incident.ownerInitials ? (
                  <span className="flex size-5 items-center justify-center rounded-full bg-[#e9dec8] text-[8px] font-semibold text-[#9b650e]">
                    {incident.ownerInitials}
                  </span>
                ) : null}
                <p className="text-[11px]">{incident.owner}</p>
              </div>
            ) : (
              <span />
            )}
            <p className="text-[10px] text-[#687180]">{incident.updated}</p>
          </div>
        </div>
      </div>

      <div className="flex w-full shrink-0 flex-col gap-4 xl:w-[396px]">
        <div className="flex items-center justify-between">
          <h2 className="text-[14px] font-semibold">Release safeguards</h2>
          <p className="text-[11px] text-[#687180]">{safeguards.summary}</p>
        </div>
        <ul className="flex flex-col gap-[13px]">
          {safeguards.checks.map((check) => (
            <li key={check.id} className="flex items-center gap-2">
              <HealthIcon
                name={check.status === "holding" ? "circlePause" : "circleCheck"}
              />
              <p className="min-w-0 flex-1 text-[12px]">{check.label}</p>
              <p
                className={`text-[11px] ${
                  check.status === "holding"
                    ? "font-medium text-[#9b650e]"
                    : "text-[#687180]"
                }`}
              >
                {check.value}
              </p>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-1.5 border-t border-[#e4e7ec] pt-3">
          <HealthIcon name="users" />
          <p className="text-[11px] text-[#687180]">{safeguards.owner}</p>
        </div>
      </div>
    </section>
  );
}

/**
 * @param {{ footer: typeof healthSnapshot.footer }} props
 */
function BoardFooter({ footer }) {
  return (
    <footer className="flex items-center justify-between gap-4 border-t border-[#e4e7ec] bg-[#f8f9fb] px-7 py-3.5">
      <div className="flex items-center gap-[7px]">
        <HealthIcon name="info" />
        <p className="text-[11px] text-[#687180]">{footer.notice}</p>
      </div>
      <p className="text-[11px] text-[#687180]">{footer.window}</p>
    </footer>
  );
}

/**
 * @param {{ tone?: "healthy" | "warning" | "neutral", icon: string, label: string }} props
 */
function StatusBadge({ tone, icon, label }) {
  const warning = tone === "warning";
  const neutral = tone === "neutral";

  return (
    <span
      className={`inline-flex items-center gap-[5px] rounded-md px-2 py-1 text-[11px] font-semibold ${
        warning
          ? "bg-[#fff6e5] text-[#9b650e]"
          : neutral
            ? "bg-[#f8f9fb] text-[#687180]"
            : "bg-[#edf7f1] text-[#237553]"
      }`}
    >
      <HealthIcon name={icon} />
      {label}
    </span>
  );
}

/**
 * Link used by the release and incident rows.
 * Without href it stays a button so the demo snapshot does not navigate.
 * @param {{ label: string, href?: string }} props
 */
function TextLink({ label, href }) {
  const className =
    "inline-flex items-center gap-[5px] text-[12px] font-medium text-[#4d5bd1]";

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={className}
      >
        {label}
        <HealthIcon name="arrowUpRight" />
      </a>
    );
  }

  return (
    <button type="button" className={className}>
      {label}
      <HealthIcon name="arrowUpRight" />
    </button>
  );
}
